import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AddressDetailsCard from './components/AddressDetailsCard';
import RiskFactorsPanel from './components/RiskFactorsPanel';
import TransactionHistoryTable from './components/TransactionHistoryTable';
import ActivityTimelineChart from './components/ActivityTimelineChart';
import BatchAssessmentPanel from './components/BatchAssessmentPanel';
import AddressCategorizationTool from './components/AddressCategorizationTool';
import VisualComponent from './components/Visual';

import { blockchainService } from '../../services/blockchainService';
import { addressService } from '../../services/investigationService';

const AddressRiskAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAddress, setCurrentAddress] = useState('');
  const [currentNetwork, setCurrentNetwork] = useState('bitcoin');
  const [searchAddress, setSearchAddress] = useState('');
  const [searchNetwork, setSearchNetwork] = useState('bitcoin');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  // Real data state
  const [addressData, setAddressData] = useState(null);
  const [riskFactors, setRiskFactors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [timelineData, setTimelineData] = useState([]);

  const handleAddressSearch = async () => {
    if (!searchAddress?.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use real blockchain service to analyze address
      const result = await blockchainService?.analyzeAddress(searchAddress?.trim(), searchNetwork);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }
      
      // Update state with real data
      setCurrentAddress(searchAddress?.trim());
      setCurrentNetwork(searchNetwork);
      setAddressData(result?.address);
      setRiskFactors(result?.riskFactors || []);
      setTransactions(result?.transactions || []);
      
      // // Generate timeline data from transactions
      if (result?.transactions?.length > 0) {
        setTimelineData(generateTimelineData(result?.transactions));
      }
      
    } catch (error) {
      setError('Failed to analyze address. Please check the address format and try again.');
      console.error('Address analysis error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateTimelineData = (transactions) => {
    // Group transactions by month for timeline chart
    const monthlyData = {};
    
    transactions?.forEach(tx => {
      const date = new Date(tx?.timestamp);
      const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      
      if (!monthlyData?.[monthKey]) {
        monthlyData[monthKey] = {
          date: new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' })?.format(date),
          transactionCount: 0,
          volume: 0
        };
      }
      
      monthlyData[monthKey].transactionCount++;
      monthlyData[monthKey].volume += parseFloat(tx?.amount || 0);
    });
    
    return Object.values(monthlyData)?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const handleBatchAssessment = async (addresses) => {
    setIsLoading(true);
    
    try {
      const result = await blockchainService?.analyzeBatchAddresses(addresses);
      console.log('Batch assessment results:', result);
      
      // Handle batch results - could show in a modal or navigate to results page
      if (result?.results?.length > 0) {
        // Navigate to investigation dashboard with batch results
        navigate('/investigation-dashboard', { 
          state: { 
            batchResults: result?.results,
            errors: result?.errors
          }
        });
      }
    } catch (error) {
      setError('Batch assessment failed. Please try again.');
      console.error('Batch assessment error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorization = async (categorizationData) => {
    if (!addressData?.id) return;
    
    try {
      // Update address category in database
      const result = await addressService?.createOrUpdateAddress({
        ...addressData,
        category: categorizationData?.category,
        notes: categorizationData?.notes
      });
      
      if (!result?.error) {
        setAddressData(result?.data);
        console.log('Address categorization updated:', result?.data);
      }
    } catch (error) {
      setError('Failed to update address categorization.');
      console.error('Categorization error:', error);
    }
  };

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'History' },
    { id: 'timeline', label: 'Timeline', icon: 'TrendingUp' },
    { id: 'batch', label: 'Batch Assessment', icon: 'Layers' },
    { id: 'categorization', label: 'Categorization', icon: 'Tag' },
    { id: 'visual', label: 'Visualization', icon: 'Eye' }
  ];

  const supportedNetworks = blockchainService?.getSupportedNetworks();

  useEffect(() => {
    // Check if address was passed from another page
    const params = new URLSearchParams(location.search);
    const addressParam = params?.get('address');
    const networkParam = params?.get('network');
    
    if (addressParam) {
      setCurrentAddress(addressParam);
      setSearchAddress(addressParam);
    }
    
    if (networkParam) {
      setCurrentNetwork(networkParam);
      setSearchNetwork(networkParam);
    }
    
    // Auto-analyze if address was passed
    if (addressParam) {
      handleAddressSearch();
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
                <Icon name="Shield" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Address Risk Assessment</h1>
                <p className="text-text-secondary">Real-time analysis of cryptocurrency addresses for suspicious activity and criminal associations</p>
              </div>
            </div>

            {/* Address Search */}
            <div className="flex items-center space-x-4 p-4 bg-card border border-border rounded-lg shadow-forensic-card">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter cryptocurrency address to analyze (Bitcoin, Ethereum, etc.)"
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e?.target?.value)}
                  className="font-data"
                />
              </div>
              <div className="w-48">
                <select 
                  value={searchNetwork}
                  onChange={(e) => setSearchNetwork(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text-primary focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {supportedNetworks?.map(network => (
                    <option key={network?.id} value={network?.id}>
                      {network?.name} ({network?.symbol})
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleAddressSearch}
                loading={isLoading}
                disabled={!searchAddress?.trim() || isLoading}
                iconName="Search"
                iconPosition="left"
              >
                Analyze Address
              </Button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                  <p className="text-error text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => setActiveTab(item?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-forensic ${
                      activeTab === item?.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                    }`}
                  >
                    <Icon name={item?.icon} size={16} color="currentColor" />
                    <span>{item?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="space-y-6">
            {activeTab === 'overview' && addressData && (
              <>
                <AddressDetailsCard
                  address={addressData?.address}
                  balance={addressData?.balance?.toFixed(8)}
                  firstActivity={addressData?.first_activity_at ? new Date(addressData.first_activity_at)?.toLocaleDateString() : 'Unknown'}
                  lastActivity={addressData?.last_activity_at ? new Date(addressData.last_activity_at)?.toLocaleDateString() : 'Unknown'}
                  transactionCount={addressData?.transaction_count}
                  riskScore={addressData?.risk_score}
                />
                {riskFactors?.length > 0 && (
                  <RiskFactorsPanel riskFactors={riskFactors} />
                )}
              </>
            )}

            {activeTab === 'transactions' && (
              <TransactionHistoryTable transactions={transactions} />
            )}

            {activeTab === 'timeline' && timelineData?.length > 0 && (
              <ActivityTimelineChart timelineData={timelineData} />
            )}

            {activeTab === 'batch' && (
              <BatchAssessmentPanel onBatchAssess={handleBatchAssessment} />
            )}

            {activeTab === 'categorization' && addressData && (
              <AddressCategorizationTool
                address={currentAddress}
                currentCategory={addressData?.category}
                onCategorize={handleCategorization}
              />
            )}

            {activeTab === 'visual' && addressData && (
              <VisualComponent
                currentAddress={currentAddress}
              />
            )}
          </div>

          {/* Show message when no address is analyzed */}
          {!addressData && !isLoading && (
            <div className="text-center py-12">
              <Icon name="Search" size={64} color="var(--color-text-secondary)" className="mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text-primary mb-2">Ready to Analyze</h3>
              <p className="text-text-secondary">Enter a cryptocurrency address above to begin real-time risk assessment</p>
            </div>
          )}

          {/* Quick Actions */}
          {addressData && (
            <div className="mt-8 p-6 bg-card border border-border rounded-lg shadow-forensic-card">
              <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/transaction-analysis', { 
                    state: { 
                      address: currentAddress, 
                      network: currentNetwork,
                      addressData 
                    } 
                  })}
                  iconName="Search"
                  iconPosition="left"
                >
                  Analyze Transactions
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/fund-flow-mapping', { 
                    state: { 
                      address: currentAddress,
                      network: currentNetwork,
                      addressData 
                    } 
                  })}
                  iconName="GitBranch"
                  iconPosition="left"
                >
                  Map Fund Flow
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/forensic-report-generator', { 
                    state: { 
                      address: currentAddress,
                      network: currentNetwork,
                      addressData,
                      riskFactors 
                    } 
                  })}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Generate Report
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/investigation-dashboard')}
                  iconName="LayoutDashboard"
                  iconPosition="left"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AddressRiskAssessment;